import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, ExternalLink } from 'lucide-react';
import './ComparisonPage.css';

const { SITE_BASE_URL } = require('../site/siteRoutes');
const { getComparisonPage } = require('../site/comparisonContent');
const { structuredDataForRoute } = require('../site/structuredData');

type ResourceLink = {
  label: string;
  url: string;
};

type ContentSection = {
  heading: string;
  paragraphs?: string[];
  bullets?: string[];
};

type ActionLink = {
  label: string;
  url: string;
};

type ProofPoint = {
  title: string;
  text: string;
};

type ComparisonRow = {
  capability: string;
  botspot: string;
  alternative: string;
};

type ContentCard = {
  eyebrow: string;
  heading: string;
  summary: string;
  url: string;
};

type FaqItem = {
  question: string;
  answer: string;
};

type ComparisonContent = {
  eyebrow: string;
  heading: string;
  summary: string;
  sections: ContentSection[];
  primaryAction?: ActionLink;
  secondaryAction?: ActionLink;
  proofPoints?: ProofPoint[];
  comparisonRows?: ComparisonRow[];
  cards?: ContentCard[];
  faq?: FaqItem[];
  relatedLinks?: ResourceLink[];
  sources?: ResourceLink[];
  verifiedAt?: string;
};

type ComparisonRoute = {
  path: string;
  title: string;
  description: string;
  pageType: string;
  lastmod: string;
  content: ComparisonContent;
};

function ResourceAnchor({ item }: { item: ResourceLink }): React.ReactElement {
  const isExternal = /^https?:\/\//i.test(item.url);
  if (!isExternal) {
    return (
      <Link to={item.url}>
        <span>{item.label}</span>
        <ArrowRight size={16} aria-hidden="true" />
      </Link>
    );
  }

  return (
    <a href={item.url} target="_blank" rel="noreferrer">
      <span>{item.label}</span>
      <ExternalLink size={15} aria-hidden="true" />
    </a>
  );
}

export default function ComparisonPage(): React.ReactElement {
  const { pathname } = useLocation();
  const normalizedPathname = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const route = getComparisonPage(normalizedPathname) as ComparisonRoute | null;

  if (!route) {
    return (
      <section className="comparison-page comparison-page--missing bsui-scope">
        <h1>Comparison not found</h1>
        <Link to="/compare">Browse platform comparisons</Link>
      </section>
    );
  }

  const canonical = `${SITE_BASE_URL}${route.path}`;
  const structuredData = structuredDataForRoute(route);
  const backLink = route.path.startsWith('/brokers/')
    ? { label: 'All brokers', url: '/brokers' }
    : route.path !== '/compare' && route.path !== '/brokers'
      ? { label: 'All comparisons', url: '/compare' }
      : null;
  const primaryAction = route.content.primaryAction || {
    label: 'Start with BotSpot',
    url: '/sales?showLogin=1',
  };
  const secondaryAction = route.content.secondaryAction || {
    label: 'See the agent workflow',
    url: '/agents',
  };

  return (
    <article className="comparison-page bsui-scope" data-bs-theme="dark">
      <Helmet>
        <title>{route.title}</title>
        <meta name="description" content={route.description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta property="og:title" content={route.title} />
        <meta property="og:description" content={route.description} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={route.title} />
        <meta name="twitter:description" content={route.description} />
        {structuredData.map((item: object, index: number) => (
          <script type="application/ld+json" key={`structured-data-${index}`}>
            {JSON.stringify(item)}
          </script>
        ))}
      </Helmet>

      <header className="comparison-page__hero">
        <div className="comparison-page__container">
          {backLink && (
            <Link className="comparison-page__back-link" to={backLink.url}>
              <ArrowLeft size={15} aria-hidden="true" />
              {backLink.label}
            </Link>
          )}
          <p className="comparison-page__eyebrow">{route.content.eyebrow}</p>
          <h1>{route.content.heading}</h1>
          <p className="comparison-page__summary">{route.content.summary}</p>
          <div className="comparison-page__actions">
            <Link className="comparison-page__primary-action" to={primaryAction.url}>
              {primaryAction.label}
              <ArrowRight size={17} aria-hidden="true" />
            </Link>
            <Link className="comparison-page__secondary-action" to={secondaryAction.url}>
              {secondaryAction.label}
            </Link>
          </div>
          {route.content.proofPoints && (
            <div className="comparison-page__proof-grid" aria-label="BotSpot capabilities">
              {route.content.proofPoints.map((point) => (
                <section key={point.title}>
                  <h2>{point.title}</h2>
                  <p>{point.text}</p>
                </section>
              ))}
            </div>
          )}
        </div>
      </header>

      <div className="comparison-page__body comparison-page__container">
        <div className="comparison-page__content">
          {route.content.comparisonRows && (
            <section className="comparison-page__section comparison-page__comparison">
              <h2>Capability comparison</h2>
              <div className="comparison-page__table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">Capability</th>
                      <th scope="col">BotSpot</th>
                      <th scope="col">Other platform or broker</th>
                    </tr>
                  </thead>
                  <tbody>
                    {route.content.comparisonRows.map((row) => (
                      <tr key={row.capability}>
                        <th scope="row">{row.capability}</th>
                        <td>{row.botspot}</td>
                        <td>{row.alternative}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {route.content.sections.map((section) => (
            <section className="comparison-page__section" key={section.heading}>
              <h2>{section.heading}</h2>
              {section.paragraphs?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
              {section.bullets && (
                <ul>
                  {section.bullets.map((bullet) => (
                    <li key={bullet}>
                      <Check size={17} aria-hidden="true" />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          {route.content.cards && (
            <section className="comparison-page__section">
              <h2>Explore the right path</h2>
              <div className="comparison-page__card-grid">
                {route.content.cards.map((card) => (
                  <Link className="comparison-page__card" to={card.url} key={card.url}>
                    <span>{card.eyebrow}</span>
                    <h3>{card.heading}</h3>
                    <p>{card.summary}</p>
                    <strong>
                      Explore
                      <ArrowRight size={16} aria-hidden="true" />
                    </strong>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {route.content.faq && (
            <section className="comparison-page__section comparison-page__faq">
              <h2>Frequently asked questions</h2>
              {route.content.faq.map((item) => (
                <article key={item.question}>
                  <h3>{item.question}</h3>
                  <p>{item.answer}</p>
                </article>
              ))}
            </section>
          )}
        </div>

        <aside className="comparison-page__sidebar">
          {route.content.relatedLinks && (
            <nav className="comparison-page__resource-panel" aria-label="Related BotSpot resources">
              <h2>Related resources</h2>
              <ul>
                {route.content.relatedLinks.map((item) => (
                  <li key={item.url}><ResourceAnchor item={item} /></li>
                ))}
              </ul>
            </nav>
          )}

          {route.content.sources && (
            <section className="comparison-page__resource-panel">
              <h2>Sources</h2>
              <ul>
                {route.content.sources.map((item) => (
                  <li key={item.url}><ResourceAnchor item={item} /></li>
                ))}
              </ul>
              {route.content.verifiedAt && (
                <p className="comparison-page__verified">Sources verified {route.content.verifiedAt}.</p>
              )}
            </section>
          )}
        </aside>
      </div>
    </article>
  );
}
